import { type UIEvent, useEffect, useRef, useState } from 'react';
import cn from 'clsx';
import { type SeoPageLink } from '@pkgs/components/types/types';
import { PageType, TemplateType } from '@pkgs/components/types/tracking';
import { getConsentContext } from '@pkgs/components/hooks/useSearchModuleTracking';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { InlineLink } from '@ev-tech/ui-core-components/InlineLink';
import { getNavigationContext, getPageContext, getShopContext, trackNavigationEvent } from '@ev-tech/snowplow-library';

import styles from './InfoRow.module.css';

const SCROLL_THRESHOLD = 20;

export const SeoLinks = ({ links }: { links: SeoPageLink[] }) => {
  const [isShowGradient, setIsShowGradient] = useState(false);
  const [scrollPosition, setScrollPosition] = useState<'start' | 'end' | 'middle'>('start');
  const linksWrapperRef = useRef<HTMLDivElement>(null);
  const masterDataShopIds = useSearchStore((state) => state.masterDataShopIds);
  const objectTypeCategory = useSearchStore((state) => state.objectTypeCategory);
  const {
    countryCode,
    options: { language },
  } = useAppContext();
  const { isMobile } = useBreakpoint();

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const element = e.target as HTMLDivElement;
    const distanceToRightEdge = element.scrollWidth - element.scrollLeft - element.clientWidth;
    if (element.scrollLeft < SCROLL_THRESHOLD) {
      setScrollPosition('start');
    } else if (distanceToRightEdge < SCROLL_THRESHOLD) {
      setScrollPosition('end');
    } else {
      setScrollPosition('middle');
    }
  };

  useEffect(() => {
    if (linksWrapperRef.current) {
      setIsShowGradient(linksWrapperRef.current.scrollWidth > linksWrapperRef.current.offsetWidth);
    }
  }, [isMobile]);

  const trackLinkClick = (index: number, link: SeoPageLink) => {
    trackNavigationEvent({
      screenName: '',
      contexts: [
        getPageContext({
          isHQPage: true,
          type: PageType.BUYER,
          country_code: countryCode,
          page_language: language,
          template_type: TemplateType.BUYER,
        }),
        getNavigationContext({
          target_url: link.url,
          element_id: `seo-link-chip_${index + 1}-link`,
        }),
        getShopContext({
          business_division_name: objectTypeCategory,
          id: masterDataShopIds?.[0],
        }),
        getConsentContext(),
      ],
    });
  };

  return (
    <div className={styles.scrollingContainer}>
      {isShowGradient && scrollPosition !== 'start' && <div aria-hidden="true" className={cn(styles.gradient, styles.gradientRight)} />}
      <div className={styles.linksContainer} ref={linksWrapperRef} onScroll={handleScroll}>
        {links.map((link, index) => {
          return <InlineLink as="a" key={link.text} href={link.url} label={link.text} onClick={() => trackLinkClick(index, link)} />;
        })}
      </div>
      {isShowGradient && scrollPosition !== 'end' && <div aria-hidden="true" className={cn(styles.gradient, styles.gradientLeft)} />}
    </div>
  );
};
